import { supabase } from '../../lib/supabase';
import type { CRMPipelineStage, CRMAccount, CRMOpportunity, OnboardingLead, KanbanCardData } from '../../types/crm';
import { failed, oneRow } from './writeResult';
import type { CRMState } from './types';
import type { Database, Json } from '../../types/supabase';
import { getMonthlyAmount } from '../../utils/pricing';
import { mapLeadStatusToStageIndex, mapStageIndexToLeadStatus, mapAccountStatusToStageIndex } from './helpers';

type SetState = (partial: Partial<CRMState> | ((state: CRMState) => Partial<CRMState>)) => void;
type GetState = () => CRMState;

type AccountRow = Database['public']['Tables']['crm_accounts']['Row'];
type AccountInsert = Database['public']['Tables']['crm_accounts']['Insert'];
type OpportunityRow = Database['public']['Tables']['crm_opportunities']['Row'];
type OpportunityUpdate = Database['public']['Tables']['crm_opportunities']['Update'];
type PipelineStageRow = Database['public']['Tables']['crm_pipeline_stages']['Row'];
type LeadRow = Database['public']['Tables']['onboarding_leads']['Row'];

/** The opportunities query joins the full account and pipeline stage rows. */
type OpportunityRowJoined = OpportunityRow & {
  account?: AccountRow | null;
  stage?: PipelineStageRow | null;
};

/** Narrow a nullable custom_fields JSON into the domain Record shape. */
function toRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize a `crm_accounts` DB row into the domain `CRMAccount`.
 * The commercial `role`/`status` are derived from the revenue view at fetch
 * time (see accountsSlice); rows loaded here have no such context, so they
 * default to the neutral `prospect` role.
 */
function toCRMAccount(row: AccountRow): CRMAccount {
  return {
    id: row.id,
    company_id: row.company_id,
    name: row.name,
    industry: row.industry,
    website: row.website,
    logo_url: row.logo_url,
    address: row.address,
    city: row.city,
    postal_code: row.postal_code,
    country: row.country,
    siret: row.siret,
    siren: row.siren,
    naf_code: row.naf_code,
    phone: row.phone,
    role: 'prospect',
    status: 'prospect',
    notes: row.notes,
    owner_id: row.owner_id,
    parent_account_id: row.parent_account_id,
    custom_fields: toRecord(row.custom_fields),
    lat: row.lat,
    lng: row.lng,
    geocoded_at: row.geocoded_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Normalize a `crm_pipeline_stages` DB row into `CRMPipelineStage`. */
function toCRMPipelineStage(row: PipelineStageRow): CRMPipelineStage {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    order_index: row.order_index,
    color: row.color,
    default_probability: row.default_probability,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

/** Normalize a joined `crm_opportunities` DB row into `CRMOpportunity`. */
function toCRMOpportunity(row: OpportunityRowJoined): CRMOpportunity {
  return {
    id: row.id,
    name: row.name,
    account_id: row.account_id,
    stage_id: row.stage_id,
    amount: row.amount,
    currency: row.currency ?? 'EUR',
    close_date: row.close_date,
    probability: row.probability,
    status: (row.status as CRMOpportunity['status']) ?? 'open',
    owner_id: row.owner_id,
    custom_fields: toRecord(row.custom_fields),
    onboarding_lead_id: row.onboarding_lead_id,
    is_onboarding: row.is_onboarding ?? false,
    stripe_product_id: row.stripe_product_id,
    custom_product_name: row.custom_product_name,
    calculated_mrr: row.calculated_mrr,
    calculated_arr: row.calculated_arr,
    revenue_type: (row.revenue_type as CRMOpportunity['revenue_type']) ?? 'recurring',
    one_time_amount: row.one_time_amount,
    target_close_date: row.target_close_date,
    reason_lost: row.reason_lost,
    created_at: row.created_at,
    updated_at: row.updated_at,
    account: row.account ? toCRMAccount(row.account) : undefined,
    stage: row.stage ? toCRMPipelineStage(row.stage) : undefined,
  };
}

/** Normalize an `onboarding_leads` DB row into the domain `OnboardingLead`. */
function toOnboardingLead(row: LeadRow): OnboardingLead {
  return {
    id: row.id,
    company_id: row.company_id,
    company_name: row.company_name,
    siret: row.siret,
    address: row.address,
    postal_code: row.postal_code,
    city: row.city,
    country: row.country,
    contact_first_name: row.contact_first_name,
    contact_last_name: row.contact_last_name,
    contact_email: row.contact_email,
    contact_phone: row.contact_phone,
    erp: row.erp,
    erp_other: row.erp_other,
    referral: row.referral,
    referral_other: row.referral_other,
    slot_start: row.slot_start,
    slot_end: row.slot_end,
    stripe_customer_id: row.stripe_customer_id,
    stripe_subscription_id: row.stripe_subscription_id,
    selected_plan: row.selected_plan,
    status: row.status,
    user_id: row.user_id,
    crm_opportunity_id: row.crm_opportunity_id,
    account_id: row.account_id,
    churned_at: row.churned_at,
    current_onboarding_stage: row.current_onboarding_stage ?? 1,
    onboarding_type: (row.onboarding_type as OnboardingLead['onboarding_type']) ?? 'standard',
    first_value_achieved_at: row.first_value_achieved_at,
    go_live_at: row.go_live_at,
    internal_notes: row.internal_notes,
    owner_id: row.owner_id,
    lat: row.lat,
    lng: row.lng,
    geocoded_at: row.geocoded_at,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

export const createPipelineSlice = (set: SetState, get: GetState) => ({
  stages: [] as CRMPipelineStage[],
  opportunities: [] as CRMOpportunity[],
  onboardingLeads: [] as OnboardingLead[],
  kanbanCards: [] as KanbanCardData[],
  selectedOpportunity: null as CRMOpportunity | null,
  selectedLead: null as OnboardingLead | null,

  fetchStages: async () => {
    try {
      const { data, error } = await supabase
        .from('crm_pipeline_stages')
        .select('*')
        .order('order_index');

      if (error) throw error;
      set({ stages: data || [] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch stages';
      set({ error: message });
    }
  },

  fetchOpportunities: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('crm_opportunities')
        .select(`
          *,
          account:crm_accounts(*),
          stage:crm_pipeline_stages(*)
        `)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      const opportunities = ((data ?? []) as OpportunityRowJoined[]).map(toCRMOpportunity);
      set({ opportunities, loading: false });
      get().buildKanbanCards();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch opportunities';
      set({ error: message, loading: false });
    }
  },

  fetchOnboardingLeads: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('onboarding_leads')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      set({ onboardingLeads: (data ?? []).map(toOnboardingLead), loading: false });
      get().buildKanbanCards();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch leads';
      set({ error: message, loading: false });
    }
  },

  buildKanbanCards: () => {
    const { opportunities, onboardingLeads, accounts, stages, stripeSubscriptions, stripeProducts, leadProducts } = get();
    const cards: KanbanCardData[] = [];
    const normalizeCompanyName = (value?: string | null) =>
      (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const leadCompanyIds = new Set(
      onboardingLeads.map((lead) => lead.company_id).filter(Boolean)
    );
    const opportunityCompanyIds = new Set(
      opportunities.map((opp) => opp.account?.company_id).filter(Boolean)
    );

    opportunities.forEach((opp) => {
      cards.push({
        id: opp.id,
        type: 'opportunity',
        title: opp.name,
        subtitle: opp.account?.name || 'Sans compte',
        amount: opp.amount || undefined,
        stage_id: opp.stage_id || stages[0]?.id || '',
        status: opp.status,
        contact_name: opp.account?.name,
        created_at: opp.created_at,
        updated_at: opp.updated_at,
        is_onboarding: opp.is_onboarding,
        has_active_subscription: false,
        mrr: opp.calculated_mrr || undefined,
        arr: opp.calculated_arr || undefined,
        raw_data: opp,
      });
    });

    onboardingLeads.forEach((lead) => {
      if (!lead.crm_opportunity_id) {
        const stageIndex = mapLeadStatusToStageIndex(lead.status, lead.current_onboarding_stage);
        const stage = stages.find((s) => s.order_index === stageIndex);

        if (!stage) {
          console.warn('[BUILD KANBAN] No stage found for index', stageIndex, 'Lead status:', lead.status, 'Lead:', lead.id, lead.company_name);
          console.log('[BUILD KANBAN] Available stages:', stages.map(s => ({ name: s.name, order_index: s.order_index })));
          const defaultStage = stages[0];
          if (defaultStage) {
            console.warn('[BUILD KANBAN] Using default stage:', defaultStage.name);
          }
        }

        const subscription = lead.stripe_customer_id
          ? stripeSubscriptions[lead.stripe_customer_id]
          : undefined;

        const hasActiveSubscription = subscription
          ? (subscription.status === 'active' || subscription.status === 'trialing')
          : false;

        let mrr: number | undefined;
        let arr: number | undefined;

        // Priorité 1: Subscription Stripe active
        if (subscription?.price_id) {
          const product = stripeProducts.find((p) => p.stripe_price_id === subscription.price_id);
          if (product) {
            mrr = getMonthlyAmount(product);
            arr = mrr * 12;
          }
        }

        // Priorité 2: Produits associés au lead
        if (!mrr) {
          const leadProductsList = leadProducts[lead.id] || [];

          if (leadProductsList.length > 0) {
            console.log(`[MRR DEBUG] Lead "${lead.company_name}" (${lead.id}):`);
            console.log(`  - stripe_subscription_id: ${lead.stripe_subscription_id || 'aucun'}`);
            console.log(`  - ${leadProductsList.length} produit(s) associé(s)`);
            console.log(`  - Produits Stripe disponibles: ${stripeProducts.length}`);
          }

          if (leadProductsList.length > 0) {
            let calculatedMrr = 0;
            leadProductsList.forEach((lp) => {
              console.log(`  - Recherche produit: product_id="${lp.stripe_product_id}", price_id="${lp.stripe_price_id}"`);

              const product = stripeProducts.find(
                (p) => (lp.stripe_price_id && p.stripe_price_id === lp.stripe_price_id) ||
                       (lp.stripe_product_id && p.stripe_product_id === lp.stripe_product_id)
              );

              if (product) {
                const amount = getMonthlyAmount(product) * (lp.quantity || 1);
                calculatedMrr += amount;
                console.log(`  - ✅ Produit trouvé: "${product.name}" - ${amount}€/mois (interval: ${product.interval}, interval_count: ${product.interval_count}, unit_amount: ${product.unit_amount})`);
              } else {
                console.log(`  - ❌ Produit NON trouvé!`);
                console.log(`    Produits Stripe disponibles:`);
                stripeProducts.slice(0, 5).forEach(p => {
                  console.log(`      - "${p.name}": product_id="${p.stripe_product_id}", price_id="${p.stripe_price_id}"`);
                });
              }
            });
            if (calculatedMrr > 0) {
              mrr = calculatedMrr;
              arr = mrr * 12;
              console.log(`  - MRR calculé: ${mrr}€, ARR: ${arr}€`);
            }
          } else if (lead.selected_plan) {
            // Priorité 3: Plan sélectionné lors de l'inscription
            const product = stripeProducts.find(
              (p) => p.stripe_price_id === lead.selected_plan || p.stripe_product_id === lead.selected_plan
            );
            if (product) {
              mrr = getMonthlyAmount(product);
              arr = mrr * 12;
            }
          }
        }

        cards.push({
          id: lead.id,
          type: 'lead',
          title: lead.company_name,
          subtitle: `${lead.contact_first_name} ${lead.contact_last_name}`,
          stage_id: stage?.id || stages[0]?.id || '',
          status: lead.status,
          contact_name: `${lead.contact_first_name} ${lead.contact_last_name}`,
          contact_email: lead.contact_email,
          created_at: lead.created_at,
          updated_at: lead.updated_at,
          is_onboarding: lead.status === 'subscription_created',
          onboarding_stage: lead.current_onboarding_stage,
          stripe_subscription: subscription,
          has_active_subscription: hasActiveSubscription,
          mrr,
          arr,
          raw_data: lead,
        });
      }
    });

    const leadCompanyNames = new Set(
      onboardingLeads.map((lead) => normalizeCompanyName(lead.company_name)).filter(Boolean)
    );
    const opportunityCompanyNames = new Set(
      opportunities
        .map((opp) => normalizeCompanyName(opp.account?.name))
        .filter(Boolean)
    );
    const seenAccountCompanyIds = new Set<string>();
    const opportunityAccountIds = new Set(opportunities.map((opp) => opp.account_id).filter(Boolean));
    accounts.forEach((account) => {
      if (opportunityAccountIds.has(account.id)) return;
      if (account.company_id) {
        if (seenAccountCompanyIds.has(account.company_id)) return;
        seenAccountCompanyIds.add(account.company_id);
      }
      const normalizedAccountName = normalizeCompanyName(account.name);
      const hasSameCompanyId =
        (account.company_id && (leadCompanyIds.has(account.company_id) || opportunityCompanyIds.has(account.company_id)));
      if (hasSameCompanyId || leadCompanyNames.has(normalizedAccountName) || opportunityCompanyNames.has(normalizedAccountName)) {
        return;
      }

      const stageIndex = mapAccountStatusToStageIndex(account.status);
      const stage = stages.find((s) => s.order_index === stageIndex) || stages[0];

      cards.push({
        id: account.id,
        type: 'account',
        title: account.name,
        subtitle: account.industry || account.city || 'Compte CRM',
        stage_id: stage?.id || stages[0]?.id || '',
        status: account.status ?? 'prospect',
        created_at: account.created_at,
        updated_at: account.updated_at || account.created_at,
        is_onboarding: false,
        has_active_subscription: false,
        raw_data: account,
      });
    });

    set({ kanbanCards: cards });
  },

  moveCard: async (cardId: string, cardType: 'opportunity' | 'lead', newStageId: string) => {
    const { stages } = get();
    const newStage = stages.find((s) => s.id === newStageId);
    if (!newStage) {
      console.error('[MOVE CARD] Stage not found:', newStageId);
      return;
    }

    console.log('[MOVE CARD] Moving card:', {
      cardId,
      cardType,
      newStageId,
      stageName: newStage.name,
      orderIndex: newStage.order_index
    });

    set({ loading: true });
    try {
      if (cardType === 'opportunity') {
        const { error } = await supabase
          .from('crm_opportunities')
          .update({
            stage_id: newStageId,
            is_onboarding: newStage.order_index >= 9,
            updated_at: new Date().toISOString()
          })
          .eq('id', cardId);

        if (error) throw error;
      } else {
        const newStatus = mapStageIndexToLeadStatus(newStage.order_index);
        const onboardingStage = newStage.order_index === 8
          ? 0
          : newStage.order_index >= 9
            ? newStage.order_index - 8
            : 1;

        console.log('[MOVE CARD] Updating lead:', {
          cardId,
          newStatus,
          onboardingStage,
          orderIndex: newStage.order_index
        });

        const { error, data } = await supabase
          .from('onboarding_leads')
          .update({
            status: newStatus,
            current_onboarding_stage: onboardingStage,
            updated_at: new Date().toISOString()
          })
          .eq('id', cardId)
          .select();

        if (error) {
          console.error('[MOVE CARD] Error updating lead:', error);
          throw error;
        }

        console.log('[MOVE CARD] Lead updated successfully:', data);
      }

      await get().fetchOpportunities();
      await get().fetchOnboardingLeads();
      set({ loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to move card';
      console.error('[MOVE CARD] Error:', error);
      set({ error: message, loading: false });
    }
  },

  createOpportunityFromLead: async (leadId: string) => {
    const { onboardingLeads, accounts } = get();
    let { stages } = get();
    const lead = onboardingLeads.find((l) => l.id === leadId);
    if (!lead) return null;

    set({ loading: true });
    try {
      if (stages.length === 0) {
        const { data: stagesData } = await supabase
          .from('crm_pipeline_stages')
          .select('*')
          .order('order_index');
        stages = stagesData || [];
        set({ stages });
      }

      // The lead's account by key only (principle 12): its canonical link
      // onboarding_leads.account_id, then the AutoQuote company id. Never by name —
      // a name match attached leads to the wrong homonym (PLAN-DETTE 4.12).
      let accountData: CRMAccount | null =
        accounts.find((acc) => acc.id === lead.account_id) ||
        accounts.find((acc) => acc.company_id && acc.company_id === lead.company_id) ||
        null;
      if (!accountData && lead.account_id) {
        const { data: linked } = await supabase
          .from('crm_accounts')
          .select('*')
          .eq('id', lead.account_id)
          .maybeSingle();
        accountData = linked ? toCRMAccount(linked) : null;
      }

      if (!accountData) {
        const accountInsert: AccountInsert = {
          company_id: lead.company_id || null,
          name: lead.company_name,
          address: lead.address,
          city: lead.city,
          postal_code: lead.postal_code,
          country: lead.country,
          phone: lead.contact_phone,
          relationship_type: 'prospect_client',
        };

        const { data: createdAccount, error: accountError } = await supabase
          .from('crm_accounts')
          .insert(accountInsert)
          .select()
          .single();

        if (accountError) throw accountError;
        accountData = createdAccount ? toCRMAccount(createdAccount) : null;
      }

      if (!accountData) {
        throw new Error('Impossible de créer ou récupérer le compte CRM');
      }

      const { error: contactError } = await supabase
        .from('crm_contacts')
        .insert({
          account_id: accountData.id,
          first_name: lead.contact_first_name,
          last_name: lead.contact_last_name,
          email: lead.contact_email,
          phone: lead.contact_phone,
        });

      if (contactError) throw contactError;

      const pipelineStages = stages.filter(s => s.order_index <= 8);
      const firstPipelineStage = pipelineStages.find(s => s.order_index === 1) || pipelineStages[0];

      if (!firstPipelineStage) {
        throw new Error('Aucun stage de pipeline disponible');
      }

      const { data: oppData, error: oppError } = await supabase
        .from('crm_opportunities')
        .insert({
          name: `Onboarding - ${lead.company_name}`,
          account_id: accountData.id,
          stage_id: firstPipelineStage.id,
          status: 'open',
          onboarding_lead_id: lead.id,
          is_onboarding: false,
        })
        .select()
        .single();

      if (oppError) throw oppError;

      // Record the canonical lead → account link when the lead had none.
      const { error: leadUpdateError } = await supabase
        .from('onboarding_leads')
        .update({
          crm_opportunity_id: oppData.id,
          ...(lead.account_id || !accountData ? {} : { account_id: accountData.id }),
        })
        .eq('id', leadId);

      if (leadUpdateError) throw leadUpdateError;

      await get().fetchOpportunities();
      await get().fetchOnboardingLeads();
      await get().fetchAccounts();
      set({ loading: false });

      // Normalize the freshly-inserted base row to the domain shape (currency
      // defaulted, status/custom_fields narrowed); joins are absent here.
      return toCRMOpportunity(oppData);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create opportunity';
      set({ error: message, loading: false });
      return null;
    }
  },

  createOpportunityFromAccount: async (accountId: string) => {
    let { stages } = get();
    set({ loading: true });
    try {
      if (stages.length === 0) {
        const { data: stagesData } = await supabase
          .from('crm_pipeline_stages')
          .select('*')
          .order('order_index');
        stages = stagesData || [];
        set({ stages });
      }

      const { data: accountData, error: accountError } = await supabase
        .from('crm_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (accountError) throw accountError;

      const pipelineStages = stages.filter(s => s.order_index <= 8);
      const firstPipelineStage = pipelineStages.find(s => s.order_index === 1) || pipelineStages[0];

      if (!firstPipelineStage) {
        throw new Error('Aucun stage de pipeline disponible');
      }

      const { data: oppData, error: oppError } = await supabase
        .from('crm_opportunities')
        .insert({
          name: `Opportunite - ${accountData.name}`,
          account_id: accountData.id,
          stage_id: firstPipelineStage.id,
          status: 'open',
          is_onboarding: false,
        })
        .select()
        .single();

      if (oppError) throw oppError;

      await get().fetchOpportunities();
      set({ loading: false });
      return toCRMOpportunity(oppData);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create opportunity';
      set({ error: message, loading: false });
      return null;
    }
  },

  createOpportunity: async (data: { name: string; account_id: string }) => {
    let { stages } = get();
    set({ loading: true });
    try {
      if (stages.length === 0) {
        const { data: stagesData } = await supabase
          .from('crm_pipeline_stages')
          .select('*')
          .order('order_index');
        stages = stagesData || [];
        set({ stages });
      }

      const pipelineStages = stages.filter(s => s.order_index <= 8);
      const firstPipelineStage = pipelineStages.find(s => s.order_index === 1) || pipelineStages[0];

      if (!firstPipelineStage) {
        throw new Error('Aucun stage de pipeline disponible');
      }

      const { data: oppData, error: oppError } = await supabase
        .from('crm_opportunities')
        .insert({
          name: data.name,
          account_id: data.account_id,
          stage_id: firstPipelineStage.id,
          status: 'open',
          is_onboarding: false,
        })
        .select('*, account:crm_accounts(*), stage:crm_pipeline_stages(*)')
        .single();

      if (oppError) throw oppError;

      await get().fetchOpportunities();
      set({ loading: false });
      return toCRMOpportunity(oppData);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create opportunity';
      set({ error: message, loading: false });
      return null;
    }
  },

  createLeadFromOpportunity: async (opportunityId: string) => {
    set({ loading: true });
    try {
      const { data: oppData, error: oppError } = await supabase
        .from('crm_opportunities')
        .select('*, account:crm_accounts(*)')
        .eq('id', opportunityId)
        .single();

      if (oppError) throw oppError;

      if (!oppData.account) {
        throw new Error('Compte CRM manquant pour creer un lead');
      }

      const { data: contactData } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('account_id', oppData.account.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      const leadPayload = {
        // The opportunity's account IS the lead's account: without it, the
        // `sync_lead_to_account` trigger looks an account up by company/name,
        // creates one when the name differs, or rewrites the found account's
        // name/phone/address with the lead's contact fields.
        account_id: oppData.account.id,
        company_id: oppData.account.company_id || null,
        company_name: oppData.account.name || 'Sans nom',
        contact_first_name: contactData?.first_name || '',
        contact_last_name: contactData?.last_name || '',
        contact_email: contactData?.email || '',
        contact_phone: contactData?.phone || '',
        siret: '',
        referral: 'unknown',
        status: 'subscription_created',
        current_onboarding_stage: 1,
        internal_notes: `Cree depuis l'opportunite ${oppData.id}`,
        address: oppData.account.address || '',
        city: oppData.account.city || '',
        postal_code: oppData.account.postal_code || '',
        country: oppData.account.country || 'France',
        erp: 'unknown',
        onboarding_type: 'standard' as const,
        crm_opportunity_id: oppData.id,
      };

      const { data: leadData, error: leadError } = await supabase
        .from('onboarding_leads')
        .insert(leadPayload)
        .select()
        .single();

      if (leadError) throw leadError;

      const { error: oppUpdateError } = await supabase
        .from('crm_opportunities')
        .update({
          onboarding_lead_id: leadData.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', oppData.id);

      if (oppUpdateError) throw oppUpdateError;

      await get().fetchOnboardingLeads();
      await get().fetchOpportunities();
      set({ loading: false });
      return toOnboardingLead(leadData);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create onboarding lead';
      set({ error: message, loading: false });
      return null;
    }
  },

  updateOpportunity: async (id: string, data: Partial<CRMOpportunity>) => {
    set({ loading: true });
    try {
      // Strip relation-only fields and normalize custom_fields to the DB Json type.
      const { account, stage, contacts, stripe_product, custom_fields, ...rest } = data;
      void account; void stage; void contacts; void stripe_product;
      const payload: OpportunityUpdate = {
        ...rest,
        ...(custom_fields !== undefined ? { custom_fields: custom_fields as Json } : {}),
        updated_at: new Date().toISOString(),
      };
      await oneRow(supabase.from('crm_opportunities').update(payload).eq('id', id).select('id'), "L'opportunité");
    } catch (error: unknown) {
      return failed(set, error, 'Failed to update opportunity', { loading: false });
    }
    await get().fetchOpportunities();
    set({ loading: false });
    return null;
  },

  updateOnboardingLead: async (id: string, data: Partial<OnboardingLead>) => {
    set({ loading: true });
    try {
      await oneRow(supabase.from('onboarding_leads').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select('id'), 'Le lead');
    } catch (error: unknown) {
      return failed(set, error, 'Failed to update lead', { loading: false });
    }
    await get().fetchOnboardingLeads();
    set({ loading: false });
    return null;
  },

  createLead: async (data: Partial<OnboardingLead>) => {
    set({ loading: true });
    try {
      if (
        !data.contact_email ||
        !data.company_name ||
        !data.contact_first_name ||
        !data.contact_last_name ||
        !data.contact_phone ||
        !data.siret ||
        !data.address ||
        !data.postal_code ||
        !data.city
      ) {
        const missingFields = [];
        if (!data.contact_email) missingFields.push('contact_email');
        if (!data.company_name) missingFields.push('company_name');
        if (!data.contact_first_name) missingFields.push('contact_first_name');
        if (!data.contact_last_name) missingFields.push('contact_last_name');
        if (!data.contact_phone) missingFields.push('contact_phone');
        if (!data.siret) missingFields.push('siret');
        if (!data.address) missingFields.push('address');
        if (!data.postal_code) missingFields.push('postal_code');
        if (!data.city) missingFields.push('city');
        throw new Error(`Champs requis manquants: ${missingFields.join(', ')}`);
      }

      const insertPayload = {
        company_name: data.company_name || '',
        contact_first_name: data.contact_first_name || '',
        contact_last_name: data.contact_last_name || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        siret: data.siret || '',
        referral: data.referral || 'unknown',
        status: data.status || 'info_collected',
        current_onboarding_stage: data.current_onboarding_stage || 1,
        internal_notes: data.internal_notes || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        country: data.country || 'France',
        erp: data.erp || 'unknown',
        onboarding_type: data.onboarding_type || 'standard',
      };

      const {
        data: sessionData
      } = await supabase.auth.getSession();
      const isAuthenticated = Boolean(sessionData.session);

      const insertQuery = supabase
        .from('onboarding_leads')
        .insert(insertPayload);

      const { data: newLead, error } = isAuthenticated
        ? await insertQuery.select().single()
        : await insertQuery;

      if (error) {
        console.error('Erreur lors de la création du lead:', error);
        console.error('Détails de l\'erreur:', JSON.stringify(error, null, 2));
        throw error;
      }
      if (isAuthenticated) {
        await get().fetchOnboardingLeads();
      }
      set({ loading: false });
      return newLead ? toOnboardingLead(newLead) : null;
    } catch (error: unknown) {
      let message = 'Failed to create lead';
      if (error && typeof error === 'object' && 'message' in error) {
        message = String(error.message);
      } else if (error instanceof Error) {
        message = error.message;
      }
      console.error('Erreur détaillée:', error);
      set({ error: message, loading: false });
      return null;
    }
  },

  setSelectedOpportunity: (opp: CRMOpportunity | null) => set({ selectedOpportunity: opp }),
  setSelectedLead: (lead: OnboardingLead | null) => set({ selectedLead: lead }),

  deleteCard: async (cardId: string, cardType: 'opportunity' | 'lead') => {
    const { onboardingLeads, stripeSubscriptions } = get();

    if (cardType === 'lead') {
      const lead = onboardingLeads.find((l) => l.id === cardId);
      if (lead?.stripe_customer_id) {
        const subscription = stripeSubscriptions[lead.stripe_customer_id];
        if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
          return {
            success: false,
            error: 'Impossible de supprimer ce lead : un abonnement Stripe actif est lie. Annulez d\'abord l\'abonnement dans Stripe.',
          };
        }
      }

      const { error } = await supabase
        .from('onboarding_leads')
        .delete()
        .eq('id', cardId);

      if (error) {
        return { success: false, error: error.message };
      }

      await get().fetchOnboardingLeads();
      return { success: true };
    } else {
      const { error } = await supabase
        .from('crm_opportunities')
        .delete()
        .eq('id', cardId);

      if (error) {
        return { success: false, error: error.message };
      }

      await get().fetchOpportunities();
      return { success: true };
    }
  },
});
